//#region src/media/errors.ts
/** SVTA 1 [Media Content] 004 — the video is in a format we can't play (e.g. an MPEG-TS container). */
const SVTA_UNSUPPORTED_VIDEO_FORMAT = 1004;
/** SVTA 1 [Media Content] 005 — the audio counterpart of {@link SVTA_UNSUPPORTED_VIDEO_FORMAT}. */
const SVTA_UNSUPPORTED_AUDIO_FORMAT = 1005;
/**
* SVTA 4 [Content Protection] 008 — unsupported or unavailable DRM system. Used for "this source is encrypted and we
* have no decryption pipeline," which is detection, not a license failure.
*/
const SVTA_UNSUPPORTED_DRM_SYSTEM = 4008;
/**
* SVTA 4 [Content Protection] 004 — bad license request. Used when the license server could not be reached or refused
* the CDM's message: from the client there is one observable ("the exchange failed at the server"), and which side was
* at fault lives in `data`, not the code.
*/
const SVTA_BAD_LICENSE_REQUEST = 4004;
/**
* SVTA 4 [Content Protection] 010 — DRM initialization error. The key system negotiated but MediaKeys could not be
* created or attached, so decryption never became possible for this source.
*/
const SVTA_DRM_INITIALIZATION_ERROR = 4010;
/**
* SVTA 4 [Content Protection] 013 — DRM certificate error. The server (application) certificate could not be fetched or
* was rejected by the CDM; without it FairPlay can never produce a license request.
*/
const SVTA_DRM_CERTIFICATE_ERROR = 4013;
/**
* SVTA 4 [Content Protection] 016 — DRM license response rejected. The server answered **200 with a license body** and
* the CDM refused it (`MediaKeySession.update()` threw). Not a server-side rejection: a non-2xx license response throws
* inside `fetchDrm` and reports {@link SVTA_BAD_LICENSE_REQUEST} instead. So this code proves the license URL,
* credentials, and request shaping all worked, and narrows the fault to the license's own contents — a security-level
* mismatch between the challenge and the issued license, for one.
*/
const SVTA_DRM_LICENSE_RESPONSE_REJECTED = 4016;
/**
* SVTA 4 [Content Protection] 021 — failed to generate DRM license request. The CDM could not produce a license request
* from the init data (`MediaKeySession.generateRequest()` threw); nothing ever reached the server.
*/
const SVTA_DRM_LICENSE_REQUEST_GENERATION_FAILED = 4021;
/**
* SVTA 4 [Content Protection] 003 — license expired. A key's status transitioned to `expired` (short license windows,
* long sessions). Browsers queue decode on a missing key, so without this cause the expiry presents as a silent
* mid-playback stall; reported so the stall is diagnosable. Re-requesting is a policy decision that lives downstream.
*/
const SVTA_LICENSE_EXPIRED = 4003;
/**
* SVTA 4 [Content Protection] 007 — insufficient output protection. A key went `output-restricted`: the CDM refuses to
* decode onto the current output path (HDCP downgrade, an external display, screen mirroring). Presents as a black
* frame or stall with no media error — nothing else in the pipeline observes the restriction.
*/
const SVTA_INSUFFICIENT_OUTPUT_PROTECTION = 4007;
/**
* SVTA 4 [Content Protection] 014 — DRM session error. A key reported `internal-error`: the CDM failed in a way it does
* not attribute (its own defect, a corrupted key store). Reported for diagnosability; recovery is downstream.
*/
const SVTA_DRM_SESSION_ERROR = 4014;
/**
* SVTA 2 [Playback] 011 — no video track the environment can play.
*
* Covers both ways a composition can end up with nothing to select: renditions that existed and were all excluded as
* unplayable, and — where the composition composes `reportAbsentTrackType` to say it needs the type — a source carrying
* none to begin with. Deliberately one code for both, because they are the same answer to a viewer, and because the
* alternative is a code the SVTA spec doesn't define. Whether an absent type is a failure is the composition's to
* state, which is why it opts in rather than being read off the source.
*/
const SVTA_NO_SUPPORTED_VIDEO_TRACK = 2011;
/** SVTA 2 [Playback] 012 — the audio counterpart of {@link SVTA_NO_SUPPORTED_VIDEO_TRACK}. */
const SVTA_NO_SUPPORTED_AUDIO_TRACK = 2012;
/**
* SVTA 99 [Custom] 001 — this engine has no pipeline for something the source requires, so the source is unplayable
* _here_ rather than broken.
*
* Custom rather than standard because the standard codes available describe either narrower or wider things. The causes
* (1004/1005 unsupported format, 4008 unsupported DRM) say what one rendition hit; the verdicts (2011/2012 no supported
* track) say a type emptied without saying why it's unfixable. And 2039 "Manifest feature unsupported" covers features
* that are unsupported but still _playable_ — LL-HLS degrading to standard live is a 2039 — so overloading it for a
* fatal condition would make it useless for the notices it belongs on.
*
* Index `001`: the spec defines only `99000` (Unknown) for the custom category and leaves the rest to the publisher, so
* this is the first code we define. Later custom codes follow the `99CII` convention (see
* {@link SVTA_UNSUPPORTED_ENCRYPTION_METHOD}); this cross-category "can't play this" surface stays in the general
* `990XX` bucket.
*
* Five digits, and deliberately not special-cased anywhere: {@link svtaCategory} and {@link svtaIndex} decompose it
* correctly by arithmetic alone, because every standard category is below `8000` and custom starts at `99000`.
*/
const SVTA_UNSUPPORTED_PLAYBACK_FEATURE = 99001;
/**
* SVTA 99 [Custom] 408 — the source is encrypted with a scheme this engine has no decryptor for, and it is **not** a
* DRM key system: HLS `METHOD=AES-128` / `SAMPLE-AES` under the `identity` keyformat (clear-key over HTTP). The non-DRM
* counterpart of {@link SVTA_UNSUPPORTED_DRM_SYSTEM} — reported so the diagnosis names the real gap, an unsupported
* encryption method, rather than blaming a DRM system that was never involved. `data` carries the HLS `method` and
* `keyFormat`. Actually decrypting such content is a deferred feature — see
* `internal/design/spf/features/clear-key-aes.md`.
*
* **The `99CII` custom-code convention.** SVTA 2070 leaves the whole `99xxx` category to the publisher (defining only
* `99000` Unknown). Rather than number custom codes sequentially, they mirror the standard taxonomy in their index
* digits: `99` + `C` (the standard category this parallels) + `II` (the index within it). So a content-protection
* (category 4) custom cause is `994II`, and `99408` deliberately echoes standard `4008` as its non-DRM sibling.
* {@link svtaCategory} / {@link svtaIndex} still decompose it by arithmetic (category `99`, index `408`); the parallel
* is a reading convention, not something the math needs. General, cross-category custom codes stay in `990XX`.
*/
const SVTA_UNSUPPORTED_ENCRYPTION_METHOD = 99408;
/**
* The error's domain — `code / 1000`, per the spec's "divide by one thousand to obtain the error category". Works
* uniformly across the four-digit native form and the five-digit form embedding an external standard: `"03404"` is
* numerically 3404, which decomposes identically. That also makes a numeric code immune to the spec's inconsistent
* zero-padding (§Approach writes a category-unknown network error as `"0300"` where the error index implies category 3
* / index 000).
*/
function svtaCategory(code) {
	return Math.floor(code / 1e3);
}
/**
* The specific error within its category — `code % 1000`. For a five-digit code this is the embedded external value (an
* HTTP status, a VAST code).
*/
function svtaIndex(code) {
	return code % 1e3;
}
//#endregion
export { SVTA_BAD_LICENSE_REQUEST, SVTA_DRM_CERTIFICATE_ERROR, SVTA_DRM_INITIALIZATION_ERROR, SVTA_DRM_LICENSE_REQUEST_GENERATION_FAILED, SVTA_DRM_LICENSE_RESPONSE_REJECTED, SVTA_DRM_SESSION_ERROR, SVTA_INSUFFICIENT_OUTPUT_PROTECTION, SVTA_LICENSE_EXPIRED, SVTA_NO_SUPPORTED_AUDIO_TRACK, SVTA_NO_SUPPORTED_VIDEO_TRACK, SVTA_UNSUPPORTED_AUDIO_FORMAT, SVTA_UNSUPPORTED_DRM_SYSTEM, SVTA_UNSUPPORTED_ENCRYPTION_METHOD, SVTA_UNSUPPORTED_PLAYBACK_FEATURE, SVTA_UNSUPPORTED_VIDEO_FORMAT, svtaCategory, svtaIndex };
